import { Loader } from "@/layouts/signed-in/Loader/Loader";
import { getReleases } from "@/services/organization-service";
import { parseDateToUTCString } from "@/utils/transforms";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Empty, Typography } from "antd";
import { useEffect, useReducer, useState } from "react";
import "./releases.style.scss";
import { useNavigate } from "react-router-dom";
import ReleaseCardTitle from "./ReleaseCardTitle";
import ReleaseSection from "./ReleaseSection";

// Define interfaces for the release data
interface ReleaseData {
	version: string;
	date: any; // Can be Firestore Timestamp or string
	createdAt?: any; // Firestore Timestamp
	updates?: { title: string; details: string[] }[];
	bugs?: { title: string; details: string[] }[];
	other?: { title: string; details: string[] }[];
	// New automated format
	description?: string;
	notes?: string[];
	deployedBy?: string;
	commitSha?: string;
	commitMessage?: string;
	pipelineUrl?: string;
	ciPlatform?: string;
}

// Define state and action types
interface ReleasesState {
	loading: boolean;
	releases: ReleaseData[];
	error: string | null;
}

type ReleasesAction =
	| { type: "FETCH_INIT" }
	| { type: "FETCH_SUCCESS"; payload: ReleaseData[] }
	| { type: "FETCH_FAILURE"; payload: string };

// Reducer for managing state
const initialState: ReleasesState = {
	loading: true,
	releases: [],
	error: null,
};

const releaseReducer = (
	state: ReleasesState,
	action: ReleasesAction,
): ReleasesState => {
	switch (action.type) {
		case "FETCH_INIT":
			return { ...state, loading: true, error: null };
		case "FETCH_SUCCESS":
			return {
				...state,
				loading: false,
				releases: action.payload,
				error: null,
			};
		case "FETCH_FAILURE":
			return { ...state, loading: false, error: action.payload };
		default:
			throw new Error(`Unsupported action type`);
	}
};

// Custom hook for fetching releases
const useReleases = (): ReleasesState => {
	const [state, dispatch] = useReducer(releaseReducer, initialState);

	useEffect(() => {
		const fetchReleases = async () => {
			dispatch({ type: "FETCH_INIT" });
			try {
				const releasesSnap = await getReleases();
				if (releasesSnap.empty) {
					dispatch({ type: "FETCH_SUCCESS", payload: [] });
					return;
				}
				const fetchedReleases: ReleaseData[] = [];
				releasesSnap.forEach((doc) =>
					fetchedReleases.push(doc.data() as ReleaseData),
				);
				dispatch({ type: "FETCH_SUCCESS", payload: fetchedReleases });
			} catch (error: any) {
				dispatch({ type: "FETCH_FAILURE", payload: error.message });
			}
		};

		fetchReleases();
	}, []);

	return state;
};

const Releases: React.FC = () => {
	const { loading, releases, error } = useReleases();
	const [currentIndex, setCurrentIndex] = useState<number>(0);
	const navigate = useNavigate();

	if (loading) return <Loader />;
	if (error)
		return <Alert message="Error" description={error} type="error" showIcon />;
	if (!releases || releases.length === 0)
		return <Empty description="No releases found" />;

	// Get the current release based on the index
	const currentRelease = releases[currentIndex];

	// Helper function to check if release uses new automated format
	const isNewFormat = (release: ReleaseData): boolean => {
		return !!release.notes && !release.updates;
	};

	// Helper function to convert notes array to sections
	const convertNotesToSections = (
		notes: string[],
	): { title: string; details: string[] }[] => {
		// Group notes by their markdown headers (### Added, ### Changed, etc.)
		const sections: { title: string; details: string[] }[] = [];
		let currentSection: { title: string; details: string[] } | null = null;

		for (const note of notes) {
			const trimmed = note.trim();

			// Check if it's a header (starts with ###)
			if (trimmed.startsWith("###")) {
				// Save previous section if exists
				if (currentSection && currentSection.details.length > 0) {
					sections.push(currentSection);
				}
				// Start new section
				currentSection = {
					title: trimmed.replace("###", "").trim(),
					details: [],
				};
			} else if (currentSection && trimmed) {
				// Add to current section
				currentSection.details.push(trimmed);
			}
		}

		// Add last section
		if (currentSection && currentSection.details.length > 0) {
			sections.push(currentSection);
		}

		return sections.length > 0
			? sections
			: [
					{
						title: "Release Notes",
						details: notes.filter((n) => n.trim() && !n.startsWith("###")),
					},
				];
	};

	// Navigation functions
	const goToPrevious = (): void => {
		if (currentIndex > 0) {
			setCurrentIndex(currentIndex - 1);
		}
	};

	const goToNext = (): void => {
		if (currentIndex < releases.length - 1) {
			setCurrentIndex(currentIndex + 1);
		}
	};

	return (
		<div className="releases-container">
			<Typography.Title level={3} className="releases-title">
				Release Notes
			</Typography.Title>
			<div className="releases-card-wrapper">
				<Card className="releases-card" bordered={false}>
					<Button
						type="link"
						onClick={() => navigate(-1)}
						icon={<LeftOutlined />}
					>
						Back
					</Button>
					<ReleaseCardTitle
						version={currentRelease.version}
						date={
							parseDateToUTCString(
								currentRelease.date?.toDate?.() ||
									currentRelease.createdAt?.toDate?.() ||
									currentRelease.date,
							) || ""
						}
					/>

					{isNewFormat(currentRelease) ? (
						// New automated format
						<>
							{currentRelease.description && (
								<Typography.Paragraph className="release-description">
									{currentRelease.description}
								</Typography.Paragraph>
							)}
							<ReleaseSection
								title="Release Notes"
								emoji="📝"
								data={
									currentRelease.notes
										? convertNotesToSections(currentRelease.notes)
										: []
								}
								badgeColor="blue"
							/>
							{currentRelease.commitMessage && (
								<Typography.Paragraph className="release-meta">
									<strong>Commit:</strong> {currentRelease.commitMessage}
								</Typography.Paragraph>
							)}
							{currentRelease.deployedBy && (
								<Typography.Paragraph className="release-meta">
									<strong>Deployed by:</strong> {currentRelease.deployedBy}
								</Typography.Paragraph>
							)}
							{currentRelease.pipelineUrl && (
								<Typography.Paragraph className="release-meta">
									<a
										href={currentRelease.pipelineUrl}
										target="_blank"
										rel="noopener noreferrer"
									>
										View Pipeline
									</a>
								</Typography.Paragraph>
							)}
						</>
					) : (
						// Legacy format
						<>
							<ReleaseSection
								title="Updates & Improvements"
								emoji="🆕"
								data={currentRelease.updates || []}
								badgeColor="green"
							/>
							<ReleaseSection
								title="Bugs & Fixes"
								emoji="🔧"
								data={currentRelease.bugs || []}
								badgeColor="orange"
							/>
							<ReleaseSection
								title="Other Enhancements"
								emoji="✨"
								data={currentRelease.other || []}
								badgeColor="blue"
							/>
						</>
					)}
				</Card>
				<div className="navigation-buttons">
					<Button
						type="primary"
						icon={<LeftOutlined />}
						onClick={goToPrevious}
						disabled={currentIndex === 0}
						iconPosition="start"
					>
						Next
					</Button>
					<Button
						type="primary"
						icon={<RightOutlined />}
						onClick={goToNext}
						disabled={currentIndex === releases.length - 1}
						iconPosition="end"
					>
						Previous
					</Button>
				</div>
			</div>
		</div>
	);
};

export { Releases };
